import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useCrmMeta } from "../../hooks/useCrmMeta";
import StatusBadge from "../../components/StatusBadge";
import { useAuth } from "../../context/AuthContext";
import { ArrowRightLeft, Search as SearchIcon, Trash2 } from "lucide-react";

const money = (v) =>
    `₱${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// A lead may come back with an object for fields we don't fully control (denormalized snapshots
// etc). Guard against ever rendering a raw object as a React child.
const s = (v, fallback = "—") => {
    if (v == null || v === "") return fallback;
    if (typeof v === "string" || typeof v === "number") return v;
    return fallback;
};

// lead aging → label + color band
const ageInfo = (days) => {
    if (days == null) return { label: "—", cls: "text-muted-foreground bg-secondary" };
    const txt = days === 0 ? "today" : days === 1 ? "1 day" : days < 60 ? `${days} days` : `${Math.round(days / 30)} mo`;
    if (days <= 30) return { label: txt, cls: "text-emerald-600 bg-emerald-500/10", dot: "bg-emerald-500" };
    if (days <= 90) return { label: txt, cls: "text-amber-600 bg-amber-500/10", dot: "bg-amber-500" };
    return { label: txt, cls: "text-rose-600 bg-rose-500/10", dot: "bg-rose-500" };
};

const emptyForm = {
    first_name: "",
    last_name: "",
    company_name: "",
    title: "",
    email: "",
    phone: "",
    mobile: "",
    address: "",
    industry: "",
    source: "",
    status: "",
    rating: "",
    tin: "",
    estimated_value: "",
    description: "",
};

const PAGE_SIZE = 100;

export default function Leads() {
    const { meta } = useCrmMeta();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin" || user?.role === "owner";
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);
    const [status, setStatus] = useState("");
    const [store, setStore] = useState("");
    const [agent, setAgent] = useState("");
    const [aging, setAging] = useState("");
    const [sort, setSort] = useState("newest");
    const [q, setQ] = useState("");

    const [facets, setFacets] = useState({ agents: [], stores: [] });

    const [showCreate, setShowCreate] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    const [convertLead, setConvertLead] = useState(null);

    const load = async (nextOffset = offset) => {
        setLoading(true);
        setErrored(false);
        try {
            const { data } = await api.get("/crm/leads", {
                params: {
                    status: status || undefined,
                    store: store || undefined,
                    agent: agent || undefined,
                    aging: aging || undefined,
                    sort,
                    q: q || undefined,
                    limit: PAGE_SIZE,
                    offset: nextOffset,
                },
            });
            setRows(Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []));
            setTotal(typeof data?.total === "number" ? data.total : (Array.isArray(data) ? data.length : 0));
        } catch (e) {
            setErrored(true);
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    // load facets once (agents + stores for the filter dropdowns)
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/crm/leads/facets");
                setFacets({ agents: data?.agents || [], stores: data?.stores || [] });
            } catch { /* filters just stay empty */ }
        })();
    }, []);

    // reload whenever a filter changes; reset to first page
    useEffect(() => {
        setOffset(0);
        load(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, store, agent, aging, sort]);

    const search = (e) => {
        e.preventDefault();
        setOffset(0);
        load(0);
    };

    const goPage = (newOffset) => {
        setOffset(newOffset);
        load(newOffset);
    };

    const clearFilters = () => {
        setStatus(""); setStore(""); setAgent(""); setAging(""); setQ(""); setSort("newest");
        // effect will reload
    };

    const hasFilters = status || store || agent || aging || q;

    const updateForm = (key, value) => setForm((f) => ({ ...f, [key]: value }));

    const resetForm = () => {
        setForm(emptyForm);
        setEditingId(null);
        setShowCreate(false);
    };

    const openEdit = (l) => {
        setForm({ ...emptyForm, ...l, estimated_value: l.estimated_value ?? "" });
        setEditingId(l.id);
        setShowCreate(true);
    };

    const createLead = async () => {
        const first = form.first_name.trim();
        const last = form.last_name.trim();
        if (!first && !last && !form.company_name.trim()) {
            toast.error("Provide a name or company for the lead.");
            return;
        }
        setSaving(true);
        try {
            const body = {
                ...form,
                estimated_value: form.estimated_value ? Number(form.estimated_value) : null,
                status: form.status || (meta.lead_statuses || [])[0] || "New",
            };
            if (editingId) {
                await api.put(`/crm/leads/${editingId}`, body);
                toast.success("Lead updated");
            } else {
                await api.post("/crm/leads", body);
                toast.success("Lead created");
            }
            resetForm();
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const deleteLead = async (l) => {
        if (!window.confirm(`Delete lead "${leadName(l)}"? It is removed from every list and report (kept for audit).`)) return;
        try {
            await api.delete(`/crm/leads/${l.id}`);
            toast.success("Lead deleted");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const leadName = (l) => {
        const name = `${s(l.first_name, "")} ${s(l.last_name, "")}`.trim();
        return name || s(l.company_name);
    };

    return (
        <div className="p-3 md:p-6 space-y-5" data-testid="crm-leads-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="sc-section-title">CRM</div>
                    <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Leads</h1>
                </div>

                <button
                    onClick={() => setShowCreate(true)}
                    className="h-9 px-4 rounded bg-foreground text-background text-sm"
                    data-testid="lead-add-button"
                >
                    New Lead
                </button>
            </div>

            <form onSubmit={search} className="sc-card p-3 space-y-3">
                <div className="flex flex-col md:flex-row gap-3">
                    <select className="sc-input md:w-52" value={agent} onChange={(e) => setAgent(e.target.value)} data-testid="lead-filter-agent">
                        <option value="">All sales agents</option>
                        {facets.agents.map((a) => (
                            <option key={a} value={a}>{a}</option>
                        ))}
                    </select>

                    <select className="sc-input md:w-52" value={store} onChange={(e) => setStore(e.target.value)} data-testid="lead-filter-store">
                        <option value="">All stores</option>
                        {facets.stores.map((st) => (
                            <option key={st} value={st}>{st}</option>
                        ))}
                    </select>

                    <select className="sc-input md:w-44" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="lead-filter-status">
                        <option value="">All statuses</option>
                        {(meta.lead_statuses || []).map((st) => (
                            <option key={st} value={st}>{st}</option>
                        ))}
                    </select>

                    <select className="sc-input md:w-44" value={aging} onChange={(e) => setAging(e.target.value)} data-testid="lead-filter-aging">
                        <option value="">Any age</option>
                        <option value="fresh">Fresh (≤30 days)</option>
                        <option value="aging">Aging (31–90 days)</option>
                        <option value="stale">Stale (90+ days)</option>
                    </select>

                    <select className="sc-input md:w-40" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="lead-sort">
                        <option value="newest">Newest first</option>
                        <option value="oldest">Oldest first</option>
                    </select>
                </div>

                <div className="flex flex-col md:flex-row gap-3">
                    <div className="relative flex-1">
                        <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            className="sc-input pl-9"
                            placeholder="Search name, company, email…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                    </div>
                    <button type="submit" className="h-9 px-4 rounded border border-border text-sm hover:bg-secondary">
                        Search
                    </button>
                    {hasFilters && (
                        <button type="button" onClick={clearFilters} className="h-9 px-4 rounded border border-border text-sm text-muted-foreground hover:bg-secondary">
                            Clear
                        </button>
                    )}
                </div>
            </form>

            <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
                <div>
                    {loading ? "Loading…" : total === 0 ? "No leads" :
                        `Showing ${offset + 1}–${offset + rows.length} of ${total.toLocaleString("en-PH", { timeZone: "Asia/Manila" })}`}
                    {hasFilters && total > 0 ? " (filtered)" : ""}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => goPage(Math.max(0, offset - PAGE_SIZE))}
                        disabled={offset === 0 || loading}
                        className="h-8 px-3 rounded border border-border disabled:opacity-40 hover:bg-secondary"
                    >
                        Prev
                    </button>
                    <button
                        onClick={() => goPage(offset + PAGE_SIZE)}
                        disabled={offset + rows.length >= total || loading}
                        className="h-8 px-3 rounded border border-border disabled:opacity-40 hover:bg-secondary"
                    >
                        Next
                    </button>
                </div>
            </div>

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[1000px]">
                        <thead>
                            <tr>
                                <th>Name / Company</th>
                                <th>Email / Phone</th>
                                <th>Sales Agent / Store</th>
                                <th>Status</th>
                                <th>Age</th>
                                <th className="text-right">Est. Value</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={7} className="text-center py-8 text-muted-foreground">Loading…</td>
                                </tr>
                            )}

                            {!loading && errored && (
                                <tr>
                                    <td colSpan={7} className="text-center py-8 text-muted-foreground">Couldn't load leads.</td>
                                </tr>
                            )}

                            {!loading && !errored && rows.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="text-center py-8 text-muted-foreground">No leads found.</td>
                                </tr>
                            )}

                            {!loading && !errored && rows.map((l) => (
                                <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                                    <td>
                                        <div className="font-medium">{leadName(l)}</div>
                                        <div className="text-xs text-muted-foreground">{s(l.title)}{l.title && l.company_name ? " · " : ""}{l.first_name || l.last_name ? s(l.company_name, "") : ""}</div>
                                    </td>
                                    <td>
                                        <div className="text-xs">{s(l.email)}</div>
                                        <div className="text-xs text-muted-foreground font-mono">{s(l.phone || l.mobile)}</div>
                                    </td>
                                    <td>
                                        <div className="text-xs">{s(l.sales_agent_name || l.owner_name)}</div>
                                        <div className="text-xs text-muted-foreground">{s(l.store)}</div>
                                    </td>
                                    <td><StatusBadge status={s(l.status, "new")} /></td>
                                    <td>
                                        {(() => {
                                            const ai = ageInfo(l.age_days);
                                            return (
                                                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${ai.cls}`} title={l.created_at ? `Created ${new Date(l.created_at).toLocaleDateString("en-PH", { timeZone: "Asia/Manila" })}` : ""}>
                                                    {ai.dot && <span className={`w-1.5 h-1.5 rounded-full ${ai.dot}`} />}
                                                    {ai.label}
                                                </span>
                                            );
                                        })()}
                                    </td>
                                    <td className="sc-num">{l.estimated_value != null ? money(l.estimated_value) : "—"}</td>
                                    <td className="text-right">
                                        <button
                                            onClick={() => openEdit(l)}
                                            className="h-8 mr-1 inline-flex items-center gap-1 px-2 rounded-md border border-border text-xs hover:bg-secondary"
                                            data-testid={`lead-edit-${l.id}`}
                                        >
                                            Edit
                                        </button>
                                        {s(l.status, "").toLowerCase() !== "converted" && (
                                            <button
                                                onClick={() => setConvertLead(l)}
                                                className="h-8 inline-flex items-center gap-1 px-2 rounded-md border border-border text-xs hover:bg-secondary"
                                                data-testid={`lead-convert-${l.id}`}
                                            >
                                                <ArrowRightLeft className="w-3.5 h-3.5" />
                                                Convert
                                            </button>
                                        )}
                                        {isAdmin && (
                                            <button
                                                onClick={() => deleteLead(l)}
                                                className="h-8 ml-1 inline-flex items-center gap-1 px-2 rounded-md border border-border text-xs text-rose-600 hover:bg-secondary"
                                                data-testid={`lead-delete-${l.id}`}
                                                title="Delete lead (admin)"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {showCreate && (
                <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={resetForm}>
                    <div className="sc-card p-5 w-full max-w-2xl space-y-4 bg-background max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="lead-create-modal">
                        <div>
                            <div className="text-xs uppercase tracking-wider text-muted-foreground">{editingId ? "Edit Lead" : "Create Lead"}</div>
                            <div className="text-lg font-semibold">{editingId ? "Update Lead" : "New Lead"}</div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-3">
                            <Field label="First Name"><input className="sc-input" value={form.first_name} onChange={(e) => updateForm("first_name", e.target.value)} /></Field>
                            <Field label="Last Name"><input className="sc-input" value={form.last_name} onChange={(e) => updateForm("last_name", e.target.value)} /></Field>
                            <Field label="Company Name"><input className="sc-input" value={form.company_name} onChange={(e) => updateForm("company_name", e.target.value)} /></Field>
                            <Field label="TIN"><input className="sc-input" value={form.tin || ""} onChange={(e) => updateForm("tin", e.target.value)} placeholder="000-000-000-000" /></Field>
                            <Field label="Title"><input className="sc-input" value={form.title} onChange={(e) => updateForm("title", e.target.value)} /></Field>
                            <Field label="Email"><input type="email" className="sc-input" value={form.email} onChange={(e) => updateForm("email", e.target.value)} /></Field>
                            <Field label="Phone"><input className="sc-input" value={form.phone} onChange={(e) => updateForm("phone", e.target.value)} /></Field>
                            <Field label="Mobile"><input className="sc-input" value={form.mobile} onChange={(e) => updateForm("mobile", e.target.value)} /></Field>
                            <Field label="Industry"><input className="sc-input" value={form.industry} onChange={(e) => updateForm("industry", e.target.value)} /></Field>

                            <Field label="Source">
                                <select className="sc-input" value={form.source} onChange={(e) => updateForm("source", e.target.value)}>
                                    <option value="">Select source…</option>
                                    {(meta.lead_sources || []).map((src) => (
                                        <option key={src} value={src}>{src}</option>
                                    ))}
                                </select>
                            </Field>

                            <Field label="Status">
                                <select className="sc-input" value={form.status} onChange={(e) => updateForm("status", e.target.value)}>
                                    <option value="">Default</option>
                                    {(meta.lead_statuses || []).map((st) => (
                                        <option key={st} value={st}>{st}</option>
                                    ))}
                                </select>
                            </Field>

                            <Field label="Rating"><input className="sc-input" placeholder="Hot / Warm / Cold" value={form.rating} onChange={(e) => updateForm("rating", e.target.value)} /></Field>
                            <Field label="Estimated Value"><input type="number" step="0.01" className="sc-input" value={form.estimated_value} onChange={(e) => updateForm("estimated_value", e.target.value)} /></Field>

                            <Field label="Address" full><input className="sc-input" value={form.address} onChange={(e) => updateForm("address", e.target.value)} /></Field>
                            <Field label="Description" full>
                                <textarea className="sc-input h-auto py-2" rows={3} value={form.description} onChange={(e) => updateForm("description", e.target.value)} />
                            </Field>
                        </div>

                        <div className="flex justify-end gap-2">
                            <button onClick={resetForm} className="h-9 px-3 rounded border border-border text-sm">Cancel</button>
                            <button onClick={createLead} disabled={saving} className="h-9 px-4 rounded bg-foreground text-background text-sm disabled:opacity-50" data-testid="lead-save">
                                {saving ? "Saving…" : (editingId ? "Update Lead" : "Create Lead")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {convertLead && (
                <ConvertLeadModal
                    lead={convertLead}
                    meta={meta}
                    onClose={() => setConvertLead(null)}
                    onDone={async () => {
                        setConvertLead(null);
                        await load();
                    }}
                />
            )}
        </div>
    );
}

function ConvertLeadModal({ lead, meta, onClose, onDone }) {
    const leadDefaultName = `${s(lead.first_name, "")} ${s(lead.last_name, "")}`.trim() || s(lead.company_name, "Lead");
    const [accountName, setAccountName] = useState(s(lead.company_name, leadDefaultName));
    const [createDeal, setCreateDeal] = useState(true);
    const [dealName, setDealName] = useState(`${leadDefaultName} — Opportunity`);
    const [amount, setAmount] = useState(lead.estimated_value || "");
    const [stage, setStage] = useState((meta.deal_stages || [])[0]?.stage || "");
    const [closingDate, setClosingDate] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        setSaving(true);
        try {
            const payload = {
                account_name: accountName.trim() || undefined,
                create_deal: createDeal,
            };
            if (createDeal) {
                payload.deal_name = dealName.trim() || undefined;
                payload.amount = amount ? Number(amount) : undefined;
                payload.stage = stage || undefined;
                payload.closing_date = closingDate || undefined;
            }
            const { data } = await api.post(`/crm/leads/${lead.id}/convert`, payload);
            const dealNo = data?.deal?.deal_name || data?.deal?.record_no;
            toast.success(dealNo ? `Lead converted — deal "${s(dealNo)}" created` : "Lead converted");
            await onDone();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={onClose}>
            <div className="sc-card p-5 w-full max-w-lg space-y-4 bg-background" onClick={(e) => e.stopPropagation()} data-testid="lead-convert-modal">
                <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Convert Lead</div>
                    <div className="text-lg font-semibold">{leadDefaultName}</div>
                </div>

                <Field label="Account Name">
                    <input className="sc-input" value={accountName} onChange={(e) => setAccountName(e.target.value)} />
                </Field>

                <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={createDeal} onChange={(e) => setCreateDeal(e.target.checked)} />
                    Create an opportunity (deal) from this lead
                </label>

                {createDeal && (
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Deal Name" full>
                            <input className="sc-input" value={dealName} onChange={(e) => setDealName(e.target.value)} />
                        </Field>
                        <Field label="Amount">
                            <input type="number" step="0.01" className="sc-input" value={amount} onChange={(e) => setAmount(e.target.value)} />
                        </Field>
                        <Field label="Stage">
                            <select className="sc-input" value={stage} onChange={(e) => setStage(e.target.value)}>
                                {(meta.deal_stages || []).map((st) => (
                                    <option key={st.stage} value={st.stage}>{st.stage}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Closing Date" full>
                            <input type="date" className="sc-input" value={closingDate} onChange={(e) => setClosingDate(e.target.value)} />
                        </Field>
                    </div>
                )}

                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="h-9 px-3 rounded border border-border text-sm" disabled={saving}>Cancel</button>
                    <button onClick={submit} disabled={saving} className="h-9 px-4 rounded bg-foreground text-background text-sm disabled:opacity-50" data-testid="lead-convert-submit">
                        {saving ? "Converting…" : "Convert Lead"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, required = false, full = false, children }) {
    return (
        <label className={`space-y-1 block ${full ? "md:col-span-2" : ""}`}>
            <div className="text-xs font-medium">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </div>
            {children}
        </label>
    );
}
